/**
 * Reusable Button UI Component
 */

import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: ReactNode;
  readonly iconPosition?: "left" | "right";
  readonly loading?: boolean;
  readonly htmlType?: "button" | "submit" | "reset";
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
	children,
	variant = "primary",
	size = "md",
	icon,
	iconPosition = "left",
	loading = false,
	disabled,
	htmlType = "button",
	className,
	...restProps
}, ref) => {
	const isDisabled = disabled || loading;

	const baseClasses = "inline-flex items-center justify-center gap-2 rounded-lg font-semibold cursor-pointer transition-all duration-150 ease-in-out whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60";

	const variantClasses = {
		primary: "bg-[#001A41] text-white hover:bg-[#0e336c] disabled:hover:bg-[#001A41]",
		secondary: "bg-slate-200 text-slate-950 hover:bg-slate-300 disabled:hover:bg-slate-200",
		outline: "bg-transparent border border-[#001a41] text-[#001a41] hover:bg-slate-50 disabled:hover:bg-transparent",
		ghost: "bg-transparent text-[#001a41] hover:bg-slate-50 disabled:hover:bg-transparent"
	};

	const sizeClasses = {
		sm: "px-3 py-1.5 text-xs leading-5",
		md: "px-4 py-2 text-sm leading-5",
		lg: "px-5 py-2.5 text-base leading-6"
	};

	const iconOnlyClass = !children && icon ? "px-2" : "";

	const classes = [
		baseClasses,
		variantClasses[variant],
		sizeClasses[size],
		iconOnlyClass,
		className || "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<button
			ref={ref}
			type={htmlType}
			className={classes}
			disabled={isDisabled}
			{...restProps}
		>
			{loading && <span className="w-3.5 h-3.5 rounded-full animate-spin" aria-hidden="true" />}
			{icon && iconPosition === "left" && <span className="flex items-center justify-center">{icon}</span>}
			{children && <span className="flex items-center justify-center">{children}</span>}
			{icon && iconPosition === "right" && <span className="flex items-center justify-center">{icon}</span>}
		</button>
	);
});

Button.displayName = "Button";
